import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    LogOut,
    Upload,
    Trash2,
    Plus,
    Pencil,
    LayoutDashboard,
    Package,
    Tag as TagIcon,
    BarChart3,
    Search,
    ExternalLink,
    TrendingUp,
    ChevronUp,
    ChevronDown,
    Bell,
} from "lucide-react";

import Logo from "../components/Logo";
import AnalyticsPanel from "../components/AnalyticsPanel";
import EditProductDialog from "../components/EditProductDialog";
import CategoryManager from "../components/CategoryManager";
import ImageCropper from "../components/ImageCropper";
import PushPanel from "../components/PushPanel";
import { api, authHeaders, getToken, setToken, clearToken, resolveImage } from "../lib/api";
import buildInfo from "../buildInfo";

function LoginScreen({ onLogin }) {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/admin/login", { password });
            setToken(data.token);
            onLogin();
        } catch {
            toast.error("Wrong password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[100dvh] flex items-center justify-center p-6 bg-[#050505]">
            <form
                data-testid="admin-login-form"
                onSubmit={submit}
                className="w-full max-w-sm bg-[#121212] border border-white/10 rounded-2xl p-7 space-y-5"
            >
                <div className="flex flex-col items-center gap-3 mb-2">
                    <Logo className="text-2xl" />
                    <span className="text-xs uppercase tracking-[0.3em] text-white/40">
                        Admin
                    </span>
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">
                        Password
                    </label>
                    <input
                        data-testid="admin-password-input"
                        type="password"
                        required
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] outline-none"
                        placeholder="••••••••"
                    />
                </div>
                <button
                    data-testid="admin-login-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 kw-orange-bg text-black font-bold rounded-lg uppercase tracking-wider disabled:opacity-60"
                >
                    {loading ? "Signing in…" : "Enter"}
                </button>
                <Link
                    to="/"
                    className="flex items-center justify-center gap-2 text-white/50 hover:text-white text-sm"
                >
                    <ArrowLeft size={14} /> Back to shop
                </Link>
            </form>
        </div>
    );
}

function UploadForm({ onCreated, categories }) {
    const [form, setForm] = useState({
        title: "",
        price: "",
        description: "",
        buy_link: "",
        category_id: "",
    });
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [rawSrc, setRawSrc] = useState(null);
    const [rawName, setRawName] = useState("");
    const [cropOpen, setCropOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleFile = (f) => {
        if (!f) return;
        setRawName(f.name || "image.jpg");
        const url = URL.createObjectURL(f);
        setRawSrc(url);
        setCropOpen(true);
    };

    const onCropped = (cropped) => {
        setFile(cropped);
        setPreview(URL.createObjectURL(cropped));
        setCropOpen(false);
    };

    const onCropCancel = () => {
        setCropOpen(false);
    };

    const reopenCropper = () => {
        if (rawSrc) setCropOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Please choose an image");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("title", form.title);
            fd.append("price", form.price);
            fd.append("description", form.description);
            fd.append("buy_link", form.buy_link);
            if (form.category_id) fd.append("category_id", form.category_id);
            fd.append("image", file);
            const { data } = await api.post("/products", fd, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            toast.success(`Added "${data.title}"`);
            setForm({ title: "", price: "", description: "", buy_link: "", category_id: "" });
            setFile(null);
            setPreview(null);
            onCreated?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setSaving(false);
        }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <form
            data-testid="upload-form"
            onSubmit={submit}
            className="bg-[#121212] border border-white/10 rounded-2xl p-5 space-y-4"
        >
            <h2 className="font-display text-2xl uppercase tracking-tight">
                New piece
            </h2>

            <label
                data-testid="file-dropzone"
                className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl p-4 cursor-pointer hover:border-[#FF5A00] transition-colors overflow-hidden"
            >
                {preview ? (
                    <div className="w-full">
                        <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-black">
                            <img
                                src={preview}
                                alt="preview"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-white/50">
                            <span>Cropped to 3:4 · tap to choose another</span>
                            <button
                                type="button"
                                data-testid="upload-recrop"
                                onClick={(e) => {
                                    e.preventDefault();
                                    reopenCropper();
                                }}
                                className="kw-orange hover:underline"
                            >
                                Re-crop
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <Upload size={24} className="text-white/60" />
                        <span className="text-sm text-white/60">
                            Click to choose an image (you'll crop it next)
                        </span>
                    </>
                )}
                <input
                    data-testid="image-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
            </label>

            <ImageCropper
                open={cropOpen}
                src={rawSrc}
                originalName={rawName}
                onCancel={onCropCancel}
                onCrop={onCropped}
            />

            <div className="grid grid-cols-2 gap-3">
                <input
                    data-testid="title-input"
                    required
                    placeholder="Title"
                    value={form.title}
                    onChange={set("title")}
                    className="col-span-2 bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] outline-none"
                />
                <input
                    data-testid="price-input"
                    placeholder="Price (e.g. $890)"
                    value={form.price}
                    onChange={set("price")}
                    className="bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] outline-none"
                />
                <input
                    data-testid="buylink-input"
                    placeholder="Buy link (https://…)"
                    value={form.buy_link}
                    onChange={set("buy_link")}
                    className="bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] outline-none"
                />
                <textarea
                    data-testid="description-input"
                    placeholder="Description"
                    value={form.description}
                    onChange={set("description")}
                    rows={3}
                    className="col-span-2 bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] outline-none resize-none"
                />
                <select
                    data-testid="category-select"
                    value={form.category_id}
                    onChange={set("category_id")}
                    className="col-span-2 bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] outline-none"
                >
                    <option value="">No category</option>
                    {(categories || []).map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
            </div>

            <button
                data-testid="upload-submit"
                type="submit"
                disabled={saving}
                className="w-full py-3 kw-orange-bg text-black font-bold rounded-lg uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
            >
                <Plus size={16} /> {saving ? "Uploading…" : "Add piece"}
            </button>
        </form>
    );
}

function ProductList({ products, categories, onDelete, onEdit, onMove }) {
    const [query, setQuery] = useState("");
    const [catFilter, setCatFilter] = useState("");

    const catMap = Object.fromEntries((categories || []).map((c) => [c.id, c.name]));

    const isFiltering = !!query.trim() || !!catFilter;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (products || []).filter((p) => {
            if (catFilter === "__none__" && p.category_id) return false;
            if (catFilter && catFilter !== "__none__" && (p.category_id || "") !== catFilter) return false;
            if (!q) return true;
            return (
                (p.title || "").toLowerCase().includes(q) ||
                (p.price || "").toLowerCase().includes(q) ||
                (p.description || "").toLowerCase().includes(q)
            );
        });
    }, [products, query, catFilter]);

    return (
        <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                    />
                    <input
                        data-testid="product-search"
                        placeholder="Search pieces…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full bg-black border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] outline-none"
                    />
                </div>
                <select
                    data-testid="product-filter-category"
                    value={catFilter}
                    onChange={(e) => setCatFilter(e.target.value)}
                    className="bg-black border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A00] outline-none"
                >
                    <option value="">All categories</option>
                    <option value="__none__">No category</option>
                    {(categories || []).map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
            </div>

            {filtered.length === 0 ? (
                <div className="text-center text-white/40 py-10 border border-dashed border-white/10 rounded-2xl text-sm">
                    {products.length === 0
                        ? "No pieces yet — upload your first above."
                        : "No pieces match your filters."}
                </div>
            ) : (
                <div data-testid="admin-product-list" className="space-y-3">
                    {filtered.map((p, i) => (
                        <div
                            key={p.id}
                            data-testid={`admin-product-${p.id}`}
                            className="flex items-center gap-2 sm:gap-4 bg-[#121212] p-3 rounded-xl border border-white/10 hover:border-white/20 transition-colors"
                        >
                            {!isFiltering && onMove && (
                                <div className="flex flex-col -space-y-1">
                                    <button
                                        data-testid={`product-up-${p.id}`}
                                        onClick={() => onMove(p.id, -1)}
                                        disabled={i === 0}
                                        aria-label="Move up"
                                        className="p-1 rounded text-white/40 hover:text-[#FF5A00] disabled:opacity-20 disabled:hover:text-white/40"
                                    >
                                        <ChevronUp size={14} />
                                    </button>
                                    <button
                                        data-testid={`product-down-${p.id}`}
                                        onClick={() => onMove(p.id, 1)}
                                        disabled={i === filtered.length - 1}
                                        aria-label="Move down"
                                        className="p-1 rounded text-white/40 hover:text-[#FF5A00] disabled:opacity-20 disabled:hover:text-white/40"
                                    >
                                        <ChevronDown size={14} />
                                    </button>
                                </div>
                            )}
                            <img
                                src={resolveImage(p.image_url)}
                                alt={p.title}
                                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">
                                    {p.title}
                                </div>
                                <div className="text-xs text-white/50 truncate">
                                    {p.price || "—"} ·{" "}
                                    {p.buy_link || "no link"}
                                </div>
                                <div className="mt-1">
                                    <span
                                        data-testid={`product-category-${p.id}`}
                                        className={
                                            "inline-block text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border " +
                                            (p.category_id
                                                ? "border-[#FF5A00]/50 kw-orange"
                                                : "border-white/10 text-white/40")
                                        }
                                    >
                                        {p.category_id
                                            ? catMap[p.category_id] ||
                                              "Unknown"
                                            : "No category"}
                                    </span>
                                </div>
                            </div>
                            <button
                                data-testid={`edit-product-${p.id}`}
                                onClick={() => onEdit(p)}
                                aria-label="Edit"
                                className="p-2 rounded-lg text-white/50 hover:text-[#FF5A00]"
                            >
                                <Pencil size={18} />
                            </button>
                            <button
                                data-testid={`delete-product-${p.id}`}
                                onClick={() => onDelete(p)}
                                aria-label="Delete"
                                className="p-2 rounded-lg text-white/50 hover:text-[#FF5A00]"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {isFiltering && filtered.length > 1 && (
                <div className="text-[10px] uppercase tracking-widest text-white/30 text-center mt-2">
                    Reordering disabled while a filter or search is active
                </div>
            )}
        </div>
    );
}

function OverviewTab({ products, categories, onGoTo, onNewPiece }) {
    const recent = products.slice(0, 4);
    const stats = [
        { label: "Pieces", value: products.length },
        { label: "Categories", value: categories.length },
        {
            label: "Uncategorized",
            value: products.filter((p) => !p.category_id).length,
        },
    ];
    return (
        <div className="space-y-6">
            {/* Hero row */}
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 flex flex-col sm:flex-row gap-5 items-start sm:items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
                        Admin dashboard
                    </div>
                    <div className="font-display text-3xl uppercase tracking-tight">
                        Welcome back
                    </div>
                    <div className="text-white/50 text-sm mt-1">
                        Manage your drop, categories and see what your audience
                        loves.
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        data-testid="overview-new-piece"
                        onClick={onNewPiece}
                        className="px-4 py-2 kw-orange-bg text-black font-bold rounded-lg uppercase tracking-wider text-xs flex items-center gap-1"
                    >
                        <Plus size={14} /> New piece
                    </button>
                    <Link
                        to="/"
                        data-testid="overview-view-shop"
                        className="px-4 py-2 rounded-lg border border-white/15 text-xs uppercase tracking-wider text-white/70 hover:text-white hover:border-white/40 flex items-center gap-1"
                    >
                        <ExternalLink size={14} /> View shop
                    </Link>
                </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
                {stats.map((s) => (
                    <div
                        key={s.label}
                        className="bg-[#121212] border border-white/10 rounded-xl p-4"
                    >
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">
                            {s.label}
                        </div>
                        <div className="font-display text-3xl">{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Recent pieces + shortcuts */}
            <div className="grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-[#121212] border border-white/10 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-xl uppercase tracking-tight flex items-center gap-2">
                            <Package size={16} className="kw-orange" /> Recent
                            pieces
                        </h3>
                        <button
                            onClick={() => onGoTo("collection")}
                            className="text-[11px] uppercase tracking-widest text-white/50 hover:text-white"
                        >
                            View all
                        </button>
                    </div>
                    {recent.length === 0 ? (
                        <div className="text-sm text-white/40 py-8 text-center border border-dashed border-white/10 rounded-xl">
                            No pieces yet. Tap "New piece" to add your first.
                        </div>
                    ) : (
                        <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {recent.map((p) => (
                                <li key={p.id}>
                                    <div className="bg-black/40 border border-white/10 rounded-xl p-2">
                                        <img
                                            src={resolveImage(p.image_url)}
                                            alt={p.title}
                                            className="w-full aspect-square object-cover rounded-lg mb-2"
                                        />
                                        <div className="text-xs font-medium truncate">
                                            {p.title}
                                        </div>
                                        <div className="text-[10px] kw-orange truncate">
                                            {p.price || "—"}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="space-y-3">
                    <button
                        onClick={() => onGoTo("categories")}
                        className="w-full text-left bg-[#121212] border border-white/10 rounded-xl p-4 hover:border-[#FF5A00]/50 transition-colors"
                    >
                        <TagIcon size={18} className="kw-orange mb-2" />
                        <div className="font-semibold text-sm">
                            Manage categories
                        </div>
                        <div className="text-[11px] text-white/50 mt-1">
                            {categories.length} categories · reorder, rename,
                            add
                        </div>
                    </button>
                    <button
                        onClick={() => onGoTo("analytics")}
                        className="w-full text-left bg-[#121212] border border-white/10 rounded-xl p-4 hover:border-[#FF5A00]/50 transition-colors"
                    >
                        <TrendingUp size={18} className="kw-orange mb-2" />
                        <div className="font-semibold text-sm">
                            Full analytics
                        </div>
                        <div className="text-[11px] text-white/50 mt-1">
                            Swipe rates, visitors, countries
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
}

const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "collection", label: "Collection", icon: Package },
    { id: "categories", label: "Categories", icon: TagIcon },
    { id: "push", label: "Push", icon: Bell },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function AdminPage() {
    const [authed, setAuthed] = useState(!!getToken());
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null);
    const [tab, setTab] = useState("overview");
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/products");
            setProducts(data);
        } catch {
            /* ignore */
        }
    };

    const loadCategories = async () => {
        try {
            const { data } = await api.get("/categories");
            setCategories(data);
        } catch {
            /* ignore */
        }
    };

    const verify = async () => {
        if (!getToken()) return;
        try {
            await api.get("/admin/verify", { headers: authHeaders() });
            setAuthed(true);
        } catch {
            clearToken();
            setAuthed(false);
        }
    };

    useEffect(() => {
        verify();
        load();
        loadCategories();
    }, []);

    const handleLogout = () => {
        clearToken();
        setAuthed(false);
        navigate("/");
    };

    const handleDelete = async (product) => {
        if (!window.confirm(`Delete "${product.title}"?`)) return;
        try {
            await api.delete(`/products/${product.id}`, {
                headers: authHeaders(),
            });
            setProducts((arr) => arr.filter((x) => x.id !== product.id));
            toast.success("Deleted");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const handleMove = async (productId, direction) => {
        const i = products.findIndex((p) => p.id === productId);
        if (i < 0) return;
        const j = i + direction;
        if (j < 0 || j >= products.length) return;
        const reordered = [...products];
        [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
        // Optimistic UI
        setProducts(reordered);
        // Persist new order indices for the entire list (sequential, 1-based)
        try {
            await Promise.all(
                reordered.map((p, idx) => {
                    const fd = new FormData();
                    fd.append("order", String(idx + 1));
                    return api.patch(`/products/${p.id}`, fd, {
                        headers: {
                            ...authHeaders(),
                            "Content-Type": "multipart/form-data",
                        },
                    });
                })
            );
        } catch {
            toast.error("Reorder failed — reloading");
            await load();
        }
    };

    if (!authed) {
        return (
            <LoginScreen
                onLogin={() => {
                    setAuthed(true);
                    load();
                }}
            />
        );
    }

    return (
        <div className="min-h-[100dvh] bg-[#050505] text-white">
            <header
                data-testid="admin-header"
                className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5"
            >
                <div className="flex items-center justify-between px-5 py-4 max-w-6xl mx-auto">
                    <Link to="/" className="flex items-center gap-3">
                        <Logo className="text-2xl" />
                        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 border border-white/20 rounded-full px-2 py-0.5">
                            Admin
                        </span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <span
                            data-testid="admin-version-badge"
                            title={`Built ${new Date(buildInfo.builtAt).toLocaleString()}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/15 text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-white/40 font-mono"
                        >
                            v{buildInfo.version}
                        </span>
                        <Link
                            to="/"
                            data-testid="admin-view-shop"
                            className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 text-[11px] uppercase tracking-widest text-white/60 hover:text-white hover:border-white/40"
                        >
                            <ExternalLink size={12} /> Shop
                        </Link>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={handleLogout}
                            className="p-2 rounded-full border border-white/10 text-white/60 hover:text-[#FF5A00] hover:border-[#FF5A00]"
                            aria-label="Logout"
                        >
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>

                <nav
                    data-testid="admin-tabs"
                    className="max-w-6xl mx-auto px-3 overflow-x-auto no-scrollbar"
                >
                    <ul className="flex gap-1 border-t border-white/5">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = t.id === tab;
                            return (
                                <li key={t.id}>
                                    <button
                                        data-testid={`admin-tab-${t.id}`}
                                        onClick={() => setTab(t.id)}
                                        className={
                                            "flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] border-b-2 transition-colors " +
                                            (active
                                                ? "border-[#FF5A00] kw-orange"
                                                : "border-transparent text-white/50 hover:text-white")
                                        }
                                    >
                                        <Icon size={14} />
                                        {t.label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-6 sm:py-8">
                {tab === "overview" && (
                    <div data-testid="tab-overview">
                        <OverviewTab
                            products={products}
                            categories={categories}
                            onGoTo={setTab}
                            onNewPiece={() => setTab("collection")}
                        />
                    </div>
                )}

                {tab === "collection" && (
                    <div
                        data-testid="tab-collection"
                        className="grid gap-6 md:grid-cols-5"
                    >
                        <div className="md:col-span-2">
                            <UploadForm
                                categories={categories}
                                onCreated={(p) =>
                                    setProducts((arr) => [p, ...arr])
                                }
                            />
                        </div>
                        <div className="md:col-span-3">
                            <div className="flex items-baseline justify-between mb-4">
                                <h2 className="font-display text-2xl uppercase tracking-tight">
                                    Collection
                                </h2>
                                <span className="text-xs text-white/40">
                                    {products.length} total
                                </span>
                            </div>
                            <ProductList
                                products={products}
                                categories={categories}
                                onDelete={handleDelete}
                                onEdit={setEditing}
                                onMove={handleMove}
                            />
                        </div>
                    </div>
                )}

                {tab === "categories" && (
                    <div
                        data-testid="tab-categories"
                        className="max-w-2xl mx-auto"
                    >
                        <CategoryManager onChange={setCategories} />
                    </div>
                )}

                {tab === "analytics" && (
                    <div data-testid="tab-analytics">
                        <AnalyticsPanel />
                    </div>
                )}
            </main>

            <EditProductDialog
                product={editing}
                categories={categories}
                open={!!editing}
                onClose={() => setEditing(null)}
                onSaved={(updated) =>
                    setProducts((arr) =>
                        arr.map((x) => (x.id === updated.id ? updated : x))
                    )
                }
            />
        </div>
    );
}
